import {
  ClipboardCheck,
  Code,
  Footprints,
  Github,
  HelpCircle,
  Keyboard,
  LayoutGrid,
  type LucideIcon,
  Shield,
  Timer,
  User,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { CodeEditorSettings } from '@/components/user-profile/code-editor-settings'
import { LayoutsSettings } from '@/components/user-profile/layouts-settings'
import { PostmortemSettings } from '@/components/user-profile/postmortem-settings'
import { ShortcutsSettings } from '@/components/user-profile/shortcuts-settings'
import { TimerSettings } from '@/components/user-profile/timer-settings'
import { EmptySettingsSection } from './empty-settings-section'
import { SprintManagement } from './sprint-management'

/** What every host passes a section body. */
export interface SettingsSectionBodyProps {
  /**
   * Runs when the body has finished its job (e.g. a layout was applied), so a
   * modal host can close. A page host passes nothing.
   */
  onDone?: () => void
}

export interface SettingsSection {
  /** The `:section` segment of `/settings/:section`. */
  slug: string
  /** The section's heading, its nav entry and the document title. */
  label: string
  /** The nav icon, mirroring the section's Clerk UserProfile tab. */
  Icon: LucideIcon
  /** The section's body; the shell renders the heading above it. */
  Component: ComponentType<SettingsSectionBodyProps>
  /** Left out of the editor settings modal on mobile, which has no FlexLayout. */
  hideInMobileEditor?: boolean
  /**
   * Whether Clerk's profile modal has a tab with this label, so the deep-link
   * fallback can reach it. Goes away with the Clerk modal at cutover (LG-1086).
   */
  clerkTab: boolean
}

/**
 * Every section of the settings page, in nav order. Adding a section is one
 * entry here plus its component; the shell, the nav and the 404 for unknown
 * slugs all read this list. Slugs mirror the section's Clerk UserProfile tab
 * `url` so porting each tab's content in later is a one-row swap of `Component`.
 * A section's body carries no heading of its own: the settings page and the
 * editor settings modal each render `label` above it.
 */
export const settingsSections = [
  {
    slug: 'profile',
    label: 'Profile',
    Icon: User,
    Component: EmptySettingsSection,
    clerkTab: true,
  },
  {
    slug: 'security',
    label: 'Security',
    Icon: Shield,
    Component: EmptySettingsSection,
    clerkTab: true,
  },
  {
    slug: 'sprints',
    label: 'Sprint management',
    Icon: Footprints,
    Component: SprintManagement,
    clerkTab: false,
  },
  {
    slug: 'code-editor',
    label: 'Code Editor',
    Icon: Code,
    Component: CodeEditorSettings,
    clerkTab: true,
  },
  {
    slug: 'timer',
    label: 'Timer',
    Icon: Timer,
    Component: TimerSettings,
    clerkTab: true,
  },
  {
    slug: 'shortcuts',
    label: 'Shortcuts',
    Icon: Keyboard,
    Component: ShortcutsSettings,
    clerkTab: true,
  },
  {
    slug: 'postmortem',
    label: 'Postmortem',
    Icon: ClipboardCheck,
    Component: PostmortemSettings,
    clerkTab: true,
  },
  {
    slug: 'help',
    label: 'Help',
    Icon: HelpCircle,
    Component: EmptySettingsSection,
    clerkTab: true,
  },
  {
    slug: 'layouts',
    label: 'Layouts',
    Icon: LayoutGrid,
    Component: LayoutsSettings,
    hideInMobileEditor: true,
    clerkTab: true,
  },
  {
    slug: 'github-integration',
    label: 'GitHub Integration',
    Icon: Github,
    Component: EmptySettingsSection,
    clerkTab: true,
  },
] as const satisfies readonly SettingsSection[]

type DeclaredSettingsSection = (typeof settingsSections)[number]

export type SettingsSectionSlug = DeclaredSettingsSection['slug']

/** Sections the Clerk deep-link fallback can reach (see `clerkTab`). */
export type ClerkReachableSettingsSlug = Extract<
  DeclaredSettingsSection,
  { clerkTab: true }
>['slug']

/**
 * The section bare `/settings` opens. Named rather than "the first entry", and
 * typed against the list so removing that section fails to compile.
 */
export const DEFAULT_SETTINGS_SECTION: SettingsSectionSlug = 'profile'

export function settingsSectionHref(slug: SettingsSectionSlug): string {
  return `/settings/${slug}`
}

/**
 * A section's label. It equals the section's Clerk UserProfile tab label, which
 * is what lets the deep-link fallback reach the same place in the old modal.
 */
export function settingsSectionLabel(slug: SettingsSectionSlug): string {
  const section = findSettingsSection(slug)
  if (!section) throw new Error(`Unknown settings section: ${slug}`)
  return section.label
}

export function isHiddenInMobileEditor(section: SettingsSection): boolean {
  return section.hideInMobileEditor === true
}

/** The section a `:section` param names; undefined for an unknown slug. */
export function findSettingsSection(
  slug: string | undefined,
): DeclaredSettingsSection | undefined {
  const wanted = slug ?? DEFAULT_SETTINGS_SECTION
  return settingsSections.find((section) => section.slug === wanted)
}
